import { datadogRum } from '@datadog/browser-rum';

type MetricCtx = Record<string, string | number | boolean>;

/**
 * drop: not worth a RUM action (epoch timestamps, WS-only hints, debug rollup counters).
 * event: one action per call, capped per metric and session.
 * counter: summed over a 30 s window, one action per window.
 * throttle: at most one action every 5 min.
 */
export type RumMetricPolicy = 'drop' | 'event' | 'counter' | 'throttle';

export const RUM_METRIC_POLICY: Record<string, RumMetricPolicy> = {
  // drop
  fe_user_speech_end: 'drop',
  fe_first_eva_chunk: 'drop',
  fe_first_eva_play: 'drop',
  adaptation_hint_sent: 'drop',
  client_ack_sent: 'drop',
  dead_air_ping_sent: 'drop',
  ws_send_calls: 'drop',
  eva_chunks_received: 'drop',
  audio_suppress_armed_narration: 'drop',
  audio_suppress_armed_greeting: 'drop',
  audio_chunks_suppressed_outbound: 'drop',
  interrupted_rejected_no_local_speech: 'drop',
  bargein_ignored_too_early: 'drop',
  suppression_refreshed_on_first_chunk: 'drop',
  // event
  first_audio_play_latency_ms: 'event',
  first_audio_play_unconditional_ms: 'event',
  interrupted_loop_latency_ms: 'event',
  jitter_buffer_escalated: 'event',
  low_turn_decision_ms: 'event',
  low_turn_settled_ms: 'event',
  preflight_failed: 'event',
  transition_watchdog_reconnect: 'event',
  barge_in_auto_disabled: 'event',
  low_settled_failed: 'event',
  low_turn_error: 'event',
  mic_reacquire_failed: 'event',
  // counter
  affect_fold: 'counter',
  affect_profile_changed: 'counter',
  unknown_command_type: 'counter',
  schema_violation: 'counter',
  uncaught_error: 'counter',
  eva_chunks_schedule_underrun: 'counter',
  ws_audio_backpressure: 'counter',
  audio_gap_detected: 'counter',
  playback_stalled: 'counter',
  capture_stalled_recovered: 'counter',
  stale_turn: 'counter',
  mic_worklet_stall: 'counter',
  mic_track_ended: 'counter',
  // throttle
  heap_growth_warning: 'throttle',
};

const EVENT_CAP_PER_SESSION = 100;
const COUNTER_WINDOW_MS = 30_000;
const THROTTLE_MS = 5 * 60_000;

interface Bucket {
  value: number;
  samples: number;
  startedAt: number;
  unit?: 'ms' | 'count';
  ctx?: MetricCtx;
  cancel: () => void;
}

export interface RumClientMetricOptions {
  /** Flag `VITE_DD_CLIENT_METRICS_ENABLED`; read on every call so a config.json change applies. */
  enabled: () => boolean;
  now?: () => number;
  /** Returns a cancel function. */
  schedule?: (fn: () => void, ms: number) => () => void;
}

/** Runs outside the Angular zone (no change detection per tick); a no-op under Karma. */
function defaultSchedule(fn: () => void, ms: number): () => void {
  const g = globalThis as any;
  if (g.__karma__) return () => undefined;
  const id = g.Zone?.root ? g.Zone.root.run(() => setTimeout(fn, ms)) : setTimeout(fn, ms);
  return () => clearTimeout(id);
}

/**
 * Sends client metrics to RUM as `fc_client_metric` actions. No `isRumReady` guard on purpose:
 * the SDK buffers events fired before `init`. Never put candidate text, names or emails in `ctx`.
 */
export class RumClientMetricReporter {
  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly eventCounts = new Map<string, number>();
  private readonly buckets = new Map<string, Bucket>();
  private readonly lastThrottled = new Map<string, number>();
  private listening = false;

  constructor(private readonly opts: RumClientMetricOptions) {
    this.now = opts.now ?? Date.now;
    this.schedule = opts.schedule ?? defaultSchedule;
  }

  report(name: string, value: number, unit?: 'ms' | 'count', ctx?: MetricCtx): void {
    try {
      if (!this.opts.enabled()) return;
      const policy = RUM_METRIC_POLICY[name];
      if (!policy) {
        console.debug('[RUM-METRICS] unknown metric dropped', name);
        return;
      }
      this.closeExpired();
      if (policy === 'drop') return;
      if (policy === 'event') {
        const n = this.eventCounts.get(name) ?? 0;
        if (n >= EVENT_CAP_PER_SESSION) return;
        this.eventCounts.set(name, n + 1);
        this.emit(name, value, unit, ctx);
      } else if (policy === 'throttle') {
        const now = this.now();
        const last = this.lastThrottled.get(name);
        if (last !== undefined && now - last < THROTTLE_MS) return;
        this.lastThrottled.set(name, now);
        this.emit(name, value, unit, ctx);
      } else {
        this.addToBucket(name, value, unit, ctx);
      }
    } catch {
      // Telemetry never breaks the flow.
    }
  }

  /** Emits every open counter window now (disconnect, page hidden). */
  flush(): void {
    for (const name of [...this.buckets.keys()]) this.closeBucket(name);
  }

  /** New interview session: clears event caps, throttles and open windows (without emitting). */
  reset(): void {
    this.buckets.forEach(b => b.cancel());
    this.buckets.clear();
    this.eventCounts.clear();
    this.lastThrottled.clear();
  }

  private addToBucket(name: string, value: number, unit?: 'ms' | 'count', ctx?: MetricCtx): void {
    let b = this.buckets.get(name);
    if (!b) {
      b = { value: 0, samples: 0, startedAt: this.now(), unit, cancel: this.schedule(() => this.closeBucket(name), COUNTER_WINDOW_MS) };
      this.buckets.set(name, b);
      this.listenForPageHide();
    }
    b.value += value;
    b.samples++;
    if (ctx) b.ctx = ctx;
  }

  /** Lazy close for when the timer did not run (Karma, throttled background tab). */
  private closeExpired(): void {
    const now = this.now();
    this.buckets.forEach((b, name) => {
      if (now - b.startedAt >= COUNTER_WINDOW_MS) this.closeBucket(name);
    });
  }

  private closeBucket(name: string): void {
    const b = this.buckets.get(name);
    if (!b) return;
    this.buckets.delete(name);
    b.cancel();
    try {
      this.emit(name, b.value, b.unit, { ...b.ctx, samples: b.samples, window_ms: this.now() - b.startedAt });
    } catch {
      // Telemetry never breaks the flow.
    }
  }

  private emit(metric: string, value: number, unit?: 'ms' | 'count', ctx?: MetricCtx): void {
    datadogRum.addAction('fc_client_metric', { ...ctx, metric, value, ...(unit ? { unit } : {}) });
  }

  private listenForPageHide(): void {
    if (this.listening || typeof document === 'undefined') return;
    this.listening = true;
    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') this.flush();
    };
    document.addEventListener('visibilitychange', flushIfHidden);
    window.addEventListener('pagehide', () => this.flush());
  }
}
